import React, { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { toast } from 'sonner';
import axios from 'axios';
import { Shield, Plus, FileText, Users, CheckCircle, Clock, AlertCircle, CreditCard, Banknote, QrCode } from 'lucide-react';
import AddressAutocomplete from './AddressAutocomplete';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// ── SA ID helpers ──────────────────────────────────────────────────────────
const validateSAId = (id) => {
  if (!id || id.length !== 13 || !/^\d{13}$/.test(id)) return false;
  // Luhn check
  let sum = 0;
  for (let i = 0; i < 13; i++) {
    let d = parseInt(id[i]);
    if (i % 2 === 1) { d *= 2; if (d > 9) d -= 9; }
    sum += d;
  }
  return sum % 10 === 0;
};

const dobFromSAId = (id) => {
  if (!id || id.length < 6) return '';
  const yy = id.slice(0, 2);
  const mm = id.slice(2, 4);
  const dd = id.slice(4, 6);
  const year = parseInt(yy) <= new Date().getFullYear() % 100 ? `20${yy}` : `19${yy}`;
  const date = new Date(`${year}-${mm}-${dd}`);
  if (isNaN(date.getTime())) return '';
  return `${year}-${mm}-${dd}`;
};

const validatePhone = (phone) => /^(\+27|0)[6-8][0-9]{8}$/.test(phone.replace(/\s/g, ''));

// Parse Google Places address_components into our fields
const parseAddressComponents = (place) => {
  const get = (types) => {
    const c = place.address_components?.find(c => types.some(t => c.types.includes(t)));
    return c ? c.long_name : '';
  };
  return {
    street: `${get(['street_number'])} ${get(['route'])}`.trim(),
    suburb: get(['sublocality_level_1', 'sublocality', 'locality']),
    province: get(['administrative_area_level_1']),
    post_code: get(['postal_code']),
  };
};

const SA_PROVINCES = [
  'Gauteng','Western Cape','KwaZulu-Natal','Eastern Cape',
  'Free State','Limpopo','Mpumalanga','North West','Northern Cape'
];

const Insurance = ({ walletBalance = 0, refreshData }) => {
  const token = localStorage.getItem('mojo_token');

  const [showNewPolicyDialog, setShowNewPolicyDialog] = useState(false);
  const [showPaymentConfirmDialog, setShowPaymentConfirmDialog] = useState(false);
  const [policies, setPolicies] = useState([]);
  const [loadingPolicies, setLoadingPolicies] = useState(true);
  const [calculatedPremium, setCalculatedPremium] = useState(null);
  const [calculating, setCalculating] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [paymentConfirmed, setPaymentConfirmed] = useState(false);
  const [cardPaymentTxnId, setCardPaymentTxnId] = useState(null);
  const [processingCard, setProcessingCard] = useState(false);
  const [cardData, setCardData] = useState({ card_number: '', expiry_date: '', cvv: '', card_type: 'debit' });
  const [fieldErrors, setFieldErrors] = useState({});

  const [formData, setFormData] = useState({
    policyholder_name: '',
    policyholder_surname: '',
    policyholder_id: '',
    policyholder_dob: '',
    cell_number: '',
    street: '',
    suburb: '',
    province: 'Gauteng',
    post_code: '',
    plan_type: 'single',
    cover_option: 'worker',
    employment_status: 'employed',
    include_spouse: false,
    spouse_name: '',
    spouse_id: '',
    spouse_dob: '',
    num_children: 0,
    children: [],
    payment_method: 'card'
  });

  useEffect(() => { fetchPolicies(); }, []);

  const fetchPolicies = async () => {
    try {
      const response = await axios.get(`${API}/insurance/policies`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setPolicies(response.data.policies || []);
    } catch (error) {
      toast.error('Failed to fetch policies');
    } finally {
      setLoadingPolicies(false);
    }
  };

  // ── Field change helpers ───────────────────────────────────────────────
  const setField = (key, value) => {
    setFormData(f => ({ ...f, [key]: value }));
    setFieldErrors(e => ({ ...e, [key]: undefined }));
  };

  const handleIdChange = (value) => {
    const digits = value.replace(/\D/g, '').slice(0, 13);
    setField('policyholder_id', digits);
    if (digits.length === 13) {
      if (!validateSAId(digits)) {
        setFieldErrors(e => ({ ...e, policyholder_id: 'Invalid SA ID number' }));
      } else {
        const dob = dobFromSAId(digits);
        setFormData(f => ({ ...f, policyholder_id: digits, policyholder_dob: dob }));
        setFieldErrors(e => ({ ...e, policyholder_id: undefined }));
      }
    }
  };

  const handleDependentIdChange = (index, value) => {
    const digits = value.replace(/\D/g, '').slice(0, 13);
    const updatedChildren = [...formData.children];
    updatedChildren[index] = { ...updatedChildren[index], id_number: digits };
    if (digits.length === 13) {
      if (!validateSAId(digits)) {
        setFieldErrors(e => ({ ...e, [`child_id_${index}`]: 'Invalid SA ID number' }));
      } else {
        updatedChildren[index].dob = dobFromSAId(digits);
        setFieldErrors(e => ({ ...e, [`child_id_${index}`]: undefined }));
      }
    }
    setFormData(f => ({ ...f, children: updatedChildren }));
  };

  const handleSpouseIdChange = (value) => {
    const digits = value.replace(/\D/g, '').slice(0, 13);
    if (digits.length === 13 && validateSAId(digits)) {
      setFormData(f => ({ ...f, spouse_id: digits, spouse_dob: dobFromSAId(digits) }));
      setFieldErrors(e => ({ ...e, spouse_id: undefined }));
    } else {
      setField('spouse_id', digits);
      if (digits.length === 13)
        setFieldErrors(e => ({ ...e, spouse_id: 'Invalid SA ID number' }));
    }
  };

  const handleCoverOptionChange = (value) => {
    if (value === 'social_grant' && formData.employment_status === 'employed') {
      toast.error('Social Grant cover is not available for employed policyholders');
      return;
    }
    setField('cover_option', value);
  };

  const handleEmploymentChange = (value) => {
    setField('employment_status', value);
    // Auto-switch away from social_grant if they change to employed
    if (value === 'employed' && formData.cover_option === 'social_grant') {
      setField('cover_option', 'worker');
      toast.info('Cover option changed to Worker — Social Grant is not available for employed status');
    }
  };

  // When Google Places selects an address, parse and fill all address fields
  const handleAddressSelect = (location) => {
    if (!location) return;
    // location comes from AddressAutocomplete's onLocationSelect — but we need
    // full address_components, so we also hook into onChange below
  };

  const handleAddressPlaceSelect = (place) => {
    if (!place?.address_components) return;
    const parsed = parseAddressComponents(place);
    setFormData(f => ({
      ...f,
      street: parsed.street || f.street,
      suburb: parsed.suburb || f.suburb,
      province: parsed.province || f.province,
      post_code: parsed.post_code || f.post_code,
    }));
  };

  // ── Validation ─────────────────────────────────────────────────────────
  const validateForm = () => {
    const errors = {};
    if (!formData.policyholder_name.trim()) errors.policyholder_name = 'First name is required';
    if (!formData.policyholder_surname.trim()) errors.policyholder_surname = 'Surname is required';
    if (!formData.policyholder_id) {
      errors.policyholder_id = 'ID number is required';
    } else if (!validateSAId(formData.policyholder_id)) {
      errors.policyholder_id = 'Invalid SA ID number';
    }
    if (!formData.policyholder_dob) errors.policyholder_dob = 'Date of birth is required';
    if (!formData.cell_number) {
      errors.cell_number = 'Cell number is required';
    } else if (!validatePhone(formData.cell_number)) {
      errors.cell_number = 'Enter a valid SA number (e.g. +27821234567 or 0821234567)';
    }
    if (formData.cover_option === 'social_grant' && formData.employment_status === 'employed') {
      errors.cover_option = 'Social Grant cover cannot be selected for employed policyholders';
    }
    // Spouse ID if provided
    if (formData.include_spouse && formData.spouse_id && !validateSAId(formData.spouse_id)) {
      errors.spouse_id = 'Invalid SA ID number';
    }
    // Children IDs
    formData.children.forEach((child, i) => {
      if (child.id_number && !validateSAId(child.id_number)) {
        errors[`child_id_${i}`] = 'Invalid SA ID number';
      }
    });
    setFieldErrors(errors);
    return Object.keys(errors).length === 0;
  };

  // ── Premium calculation ────────────────────────────────────────────────
  const calculatePremium = async () => {
    if (!validateForm()) {
      toast.error('Please fix the form errors before calculating');
      return;
    }
    setCalculating(true);
    try {
      const response = await axios.post(
        `${API}/insurance/calculate-premium`,
        null,
        {
          params: {
            plan_type: formData.plan_type,
            cover_option: formData.cover_option,
            employment_status: formData.employment_status,
            policyholder_dob: formData.policyholder_dob,
            num_children: parseInt(formData.num_children) || 0,
            include_spouse: formData.include_spouse
          },
          headers: { Authorization: `Bearer ${token}` }
        }
      );
      setCalculatedPremium(response.data);
      toast.success(`Premium calculated: R${response.data.premium_breakdown.total_premium}`);
    } catch (error) {
      let msg = 'Failed to calculate premium';
      const detail = error.response?.data?.detail;
      if (typeof detail === 'string') msg = detail;
      else if (Array.isArray(detail)) msg = detail.map(e => e.msg || JSON.stringify(e)).join(', ');
      toast.error(msg);
    } finally {
      setCalculating(false);
    }
  };

  const handleRequestPayment = () => {
    if (!validateForm()) { toast.error('Please fix the form errors first'); return; }
    if (!calculatedPremium) { toast.error('Please calculate premium first'); return; }
    setShowPaymentConfirmDialog(true);
  };

  const handleCardPayment = async () => {
    if (!cardData.card_number || !cardData.expiry_date || !cardData.cvv) {
      toast.error('Please enter all card details');
      return;
    }
    setProcessingCard(true);
    try {
      const premium = calculatedPremium.premium_breakdown.total_premium;
      const response = await axios.post(
        `${API}/payment/card`,
        {
          amount: premium,
          card_number: cardData.card_number.replace(/\s/g, ''),
          expiry_date: cardData.expiry_date,
          cvv: cardData.cvv,
          card_type: cardData.card_type,
          description: `Insurance premium - ${formData.policyholder_name} ${formData.policyholder_surname}`
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setCardPaymentTxnId(response.data.transaction_id);
      toast.success(`Card payment approved — R${premium.toFixed(2)} collected`);
    } catch (error) {
      const detail = error.response?.data?.detail;
      toast.error(typeof detail === 'string' ? detail : 'Card payment failed');
    } finally {
      setProcessingCard(false);
    }
  };

  const handlePaymentConfirmed = () => {
    setPaymentConfirmed(true);
    setShowPaymentConfirmDialog(false);
    toast.success('Payment confirmed. Creating policy...');
    handleSubmitPolicy(true);
  };

  const handleSubmitPolicy = async (confirmedNow = false) => {
    if (!confirmedNow && !paymentConfirmed) {
      toast.error('Please confirm payment collection first');
      return;
    }
    const dependents = [];
    if (formData.include_spouse && formData.spouse_name) {
      dependents.push({ name: formData.spouse_name, id_number: formData.spouse_id || null, date_of_birth: formData.spouse_dob || null, relationship: 'spouse' });
    }
    formData.children.forEach(child => {
      if (child.name) dependents.push({ name: child.name, id_number: child.id_number || null, date_of_birth: child.dob || null, relationship: 'child' });
    });

    setSubmitting(true);
    try {
      const response = await axios.post(
        `${API}/insurance/create-policy`,
        {
          policyholder_name: formData.policyholder_name,
          policyholder_surname: formData.policyholder_surname,
          policyholder_id: formData.policyholder_id,
          policyholder_dob: formData.policyholder_dob,
          cell_number: formData.cell_number,
          address: { street: formData.street, suburb: formData.suburb, province: formData.province, post_code: formData.post_code },
          plan_type: formData.plan_type,
          cover_option: formData.cover_option,
          employment_status: formData.employment_status,
          dependents,
          first_premium_amount: calculatedPremium.premium_breakdown.total_premium,
          payment_method: formData.payment_method,
          card_payment_transaction_id: cardPaymentTxnId || null,
          popi_approved: true
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(`Policy created! Commission: R${response.data.merchant_commission.toFixed(2)}`);
      setShowNewPolicyDialog(false);
      resetForm();
      fetchPolicies();
    } catch (error) {
      let msg = 'Failed to create policy';
      const detail = error.response?.data?.detail;
      if (typeof detail === 'string') msg = detail;
      else if (Array.isArray(detail)) msg = detail.map(e => e.msg || JSON.stringify(e)).join(', ');
      toast.error(msg);
    } finally {
      setSubmitting(false);
    }
  };

  const resetForm = () => {
    setFormData({
      policyholder_name: '', policyholder_surname: '', policyholder_id: '', policyholder_dob: '',
      cell_number: '', street: '', suburb: '', province: 'Gauteng', post_code: '',
      plan_type: 'single', cover_option: 'worker', employment_status: 'employed',
      include_spouse: false, spouse_name: '', spouse_id: '', spouse_dob: '',
      num_children: 0, children: [], payment_method: 'card'
    });
    setCalculatedPremium(null);
    setPaymentConfirmed(false);
    setCardPaymentTxnId(null);
    setCardData({ card_number: '', expiry_date: '', cvv: '', card_type: 'debit' });
    setFieldErrors({});
  };

  const addChild = () => {
    if (formData.children.length >= 5) { toast.error('Maximum 5 children allowed'); return; }
    setFormData(f => ({ ...f, num_children: f.children.length + 1, children: [...f.children, { name: '', id_number: '', dob: '' }] }));
  };

  const updateChild = (index, field, value) => {
    const updated = [...formData.children];
    updated[index] = { ...updated[index], [field]: value };
    setFormData(f => ({ ...f, children: updated }));
  };

  const removeChild = (index) => {
    const updated = formData.children.filter((_, i) => i !== index);
    setFormData(f => ({ ...f, num_children: updated.length, children: updated }));
  };

  const FieldError = ({ field }) => fieldErrors[field]
    ? <p className="text-xs text-red-500 mt-1">{fieldErrors[field]}</p>
    : null;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h2 className="text-2xl font-bold">Funeral Insurance</h2>
          <p className="text-gray-600">Sell funeral policies and earn commissions</p>
        </div>
        <Button onClick={() => setShowNewPolicyDialog(true)} className="bg-blue-600 hover:bg-blue-700">
          <Plus className="mr-2 h-4 w-4" /> New Policy
        </Button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card>
          <CardHeader className="pb-3"><CardTitle className="text-sm font-medium text-gray-600">Total Policies Sold</CardTitle></CardHeader>
          <CardContent><div className="text-2xl font-bold">{policies.length}</div></CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-3"><CardTitle className="text-sm font-medium text-gray-600">Active Policies</CardTitle></CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-green-600">
              {policies.filter(p => p.status === 'active' || p.status === 'pending_activation').length}
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-3"><CardTitle className="text-sm font-medium text-gray-600">Total Commissions</CardTitle></CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-blue-600">
              R{policies.reduce((sum, p) => sum + ((p.premium_amount || 0) * (p.employment_status === 'employed' ? 0.3 : 0.2) * 0.5), 0).toFixed(2)}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Policies List */}
      <Card>
        <CardHeader>
          <CardTitle>Recent Policies</CardTitle>
          <CardDescription>Funeral policies you've sold</CardDescription>
        </CardHeader>
        <CardContent>
          {loadingPolicies ? (
            <div className="text-center py-8 text-gray-500">Loading policies...</div>
          ) : policies.length === 0 ? (
            <div className="text-center py-8 text-gray-500">
              <Shield className="mx-auto h-12 w-12 text-gray-400 mb-2" />
              <p>No policies sold yet</p>
            </div>
          ) : (
            <div className="space-y-4">
              {policies.map((policy) => (
                <div key={policy.policy_id} className="border rounded-lg p-4 hover:bg-gray-50">
                  <div className="flex justify-between items-start">
                    <div className="flex-1">
                      <div className="flex items-center gap-2">
                        <h3 className="font-semibold">{policy.policyholder_name} {policy.policyholder_surname}</h3>
                        {policy.status === 'active' && <CheckCircle className="h-4 w-4 text-green-600" />}
                        {policy.status === 'pending_activation' && <Clock className="h-4 w-4 text-yellow-600" />}
                      </div>
                      <div className="text-sm text-gray-600 space-y-1 mt-1">
                        <p>Policy ID: {policy.policy_id}</p>
                        <p>Plan: {(() => { const t = policy.plan_type || policy.policy_type || ''; return t ? t.charAt(0).toUpperCase() + t.slice(1) : '—'; })()} — {policy.cover_option || '—'}</p>
                        <p>Dependents: {(policy.dependents || []).length}</p>
                        <p>Created: {new Date(policy.created_at).toLocaleDateString()}</p>
                      </div>
                    </div>
                    <div className="text-right">
                      <div className="text-lg font-bold">R{(policy.premium_amount || 0).toFixed(2)}</div>
                      <div className="text-sm text-gray-600">Premium</div>
                      <div className="text-sm text-green-600 font-medium mt-1">
                        +R{((policy.premium_amount || 0) * (policy.employment_status === 'employed' ? 0.3 : 0.2) * 0.5).toFixed(2)} commission
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* New Policy Dialog */}
      <Dialog open={showNewPolicyDialog} onOpenChange={setShowNewPolicyDialog}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>New Funeral Insurance Policy</DialogTitle>
            <DialogDescription>Capture customer details and create a new funeral policy</DialogDescription>
          </DialogHeader>

          <div className="space-y-6">
            {/* Plan Configuration */}
            <Card>
              <CardHeader><CardTitle className="text-lg">Plan Configuration</CardTitle></CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-3 gap-4">
                  <div>
                    <Label>Plan Type</Label>
                    <Select value={formData.plan_type} onValueChange={(v) => setField('plan_type', v)}>
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="single">Single</SelectItem>
                        <SelectItem value="family">Family</SelectItem>
                        <SelectItem value="single_parent">Single Parent</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  <div>
                    <Label>Employment Status</Label>
                    <Select value={formData.employment_status} onValueChange={handleEmploymentChange}>
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="employed">Employed</SelectItem>
                        <SelectItem value="not_employed">Not Employed</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  <div>
                    <Label>Cover Option</Label>
                    <Select value={formData.cover_option} onValueChange={handleCoverOptionChange}>
                      <SelectTrigger className={fieldErrors.cover_option ? 'border-red-500' : ''}><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="worker">Worker</SelectItem>
                        <SelectItem value="social_grant" disabled={formData.employment_status === 'employed'}>
                          Social Grant {formData.employment_status === 'employed' ? '(not available for employed)' : ''}
                        </SelectItem>
                      </SelectContent>
                    </Select>
                    <FieldError field="cover_option" />
                    {formData.employment_status === 'employed' && (
                      <p className="text-xs text-amber-600 mt-1">Social Grant cover is not available for employed policyholders</p>
                    )}
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Policyholder Details */}
            <Card>
              <CardHeader><CardTitle className="text-lg">Policyholder Details</CardTitle></CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>First Name *</Label>
                    <Input
                      value={formData.policyholder_name}
                      onChange={(e) => setField('policyholder_name', e.target.value)}
                      placeholder="John"
                      className={fieldErrors.policyholder_name ? 'border-red-500' : ''}
                    />
                    <FieldError field="policyholder_name" />
                  </div>
                  <div>
                    <Label>Surname *</Label>
                    <Input
                      value={formData.policyholder_surname}
                      onChange={(e) => setField('policyholder_surname', e.target.value)}
                      placeholder="Doe"
                      className={fieldErrors.policyholder_surname ? 'border-red-500' : ''}
                    />
                    <FieldError field="policyholder_surname" />
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>SA ID Number * (13 digits)</Label>
                    <Input
                      value={formData.policyholder_id}
                      onChange={(e) => handleIdChange(e.target.value)}
                      placeholder="9001015009087"
                      maxLength={13}
                      className={fieldErrors.policyholder_id ? 'border-red-500' : formData.policyholder_id.length === 13 && !fieldErrors.policyholder_id ? 'border-green-500' : ''}
                    />
                    <FieldError field="policyholder_id" />
                    {formData.policyholder_id.length === 13 && !fieldErrors.policyholder_id && (
                      <p className="text-xs text-green-600 mt-1">✓ Valid ID number</p>
                    )}
                    <p className="text-xs text-gray-400 mt-1">{formData.policyholder_id.length}/13 digits — date of birth auto-filled from ID</p>
                  </div>
                  <div>
                    <Label>Date of Birth *</Label>
                    <Input
                      type="date"
                      value={formData.policyholder_dob}
                      onChange={(e) => setField('policyholder_dob', e.target.value)}
                      className={fieldErrors.policyholder_dob ? 'border-red-500' : ''}
                    />
                    <FieldError field="policyholder_dob" />
                    {formData.policyholder_id.length === 13 && formData.policyholder_dob && (
                      <p className="text-xs text-gray-400 mt-1">Auto-extracted from ID number</p>
                    )}
                  </div>
                </div>

                <div>
                  <Label>Cell Number *</Label>
                  <Input
                    value={formData.cell_number}
                    onChange={(e) => setField('cell_number', e.target.value)}
                    placeholder="+27821234567"
                    className={fieldErrors.cell_number ? 'border-red-500' : ''}
                  />
                  <FieldError field="cell_number" />
                </div>

                {/* Address with Google Maps */}
                <div>
                  <Label>Street Address</Label>
                  <AddressAutocomplete
                    value={formData.street}
                    onChange={(e) => setField('street', e.target.value)}
                    placeholder="Start typing an address..."
                    onLocationSelect={(location) => {
                      // AddressAutocomplete passes { address, latitude, longitude }
                      // We need to get the full place — we hook via a custom approach below
                    }}
                    onPlaceSelect={handleAddressPlaceSelect}
                  />
                  <p className="text-xs text-gray-400 mt-1">Search powered by Google Maps — suburb, province & post code auto-fill</p>
                </div>

                <div className="grid grid-cols-3 gap-4">
                  <div>
                    <Label>Suburb</Label>
                    <Input
                      value={formData.suburb}
                      onChange={(e) => setField('suburb', e.target.value)}
                      placeholder="Sandton"
                    />
                  </div>
                  <div>
                    <Label>Province</Label>
                    <Select value={formData.province} onValueChange={(v) => setField('province', v)}>
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {SA_PROVINCES.map(p => <SelectItem key={p} value={p}>{p}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label>Post Code</Label>
                    <Input
                      value={formData.post_code}
                      onChange={(e) => setField('post_code', e.target.value.replace(/\D/g, '').slice(0, 4))}
                      placeholder="2196"
                      maxLength={4}
                    />
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Dependents */}
            {(formData.plan_type === 'family' || formData.plan_type === 'single_parent') && (
              <Card>
                <CardHeader>
                  <CardTitle className="text-lg">Dependents</CardTitle>
                  <CardDescription>Date of birth is auto-extracted when a valid 13-digit SA ID is entered</CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  {formData.plan_type === 'family' && (
                    <div>
                      <div className="flex items-center space-x-2 mb-4">
                        <input type="checkbox" checked={formData.include_spouse} onChange={(e) => setField('include_spouse', e.target.checked)} className="h-4 w-4" />
                        <Label>Include Spouse</Label>
                      </div>
                      {formData.include_spouse && (
                        <div className="space-y-3 border-l-2 border-blue-200 pl-4">
                          <Input placeholder="Spouse Full Name" value={formData.spouse_name} onChange={(e) => setField('spouse_name', e.target.value)} />
                          <div className="grid grid-cols-2 gap-3">
                            <div>
                              <Input
                                placeholder="SA ID Number (13 digits)"
                                value={formData.spouse_id}
                                onChange={(e) => handleSpouseIdChange(e.target.value)}
                                maxLength={13}
                                className={fieldErrors.spouse_id ? 'border-red-500' : formData.spouse_id.length === 13 && !fieldErrors.spouse_id ? 'border-green-500' : ''}
                              />
                              <FieldError field="spouse_id" />
                            </div>
                            <div>
                              <Input type="date" placeholder="Date of Birth" value={formData.spouse_dob} onChange={(e) => setField('spouse_dob', e.target.value)} />
                              {formData.spouse_id.length === 13 && formData.spouse_dob && (
                                <p className="text-xs text-gray-400 mt-1">Auto-extracted from ID</p>
                              )}
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                  )}

                  <div>
                    <div className="flex justify-between items-center mb-3">
                      <Label>Children (Max 5)</Label>
                      <Button size="sm" onClick={addChild} variant="outline"><Plus className="h-3 w-3 mr-1" /> Add Child</Button>
                    </div>
                    {formData.children.map((child, index) => (
                      <div key={index} className="space-y-2 p-3 border rounded mb-3">
                        <div className="flex justify-between items-center">
                          <span className="text-sm font-medium">Child {index + 1}</span>
                          <Button size="sm" variant="ghost" onClick={() => removeChild(index)}>Remove</Button>
                        </div>
                        <Input placeholder="Child Full Name" value={child.name} onChange={(e) => updateChild(index, 'name', e.target.value)} />
                        <div className="grid grid-cols-2 gap-2">
                          <div>
                            <Input
                              placeholder="SA ID Number (13 digits)"
                              value={child.id_number}
                              onChange={(e) => handleDependentIdChange(index, e.target.value)}
                              maxLength={13}
                              className={fieldErrors[`child_id_${index}`] ? 'border-red-500' : child.id_number.length === 13 && !fieldErrors[`child_id_${index}`] ? 'border-green-500' : ''}
                            />
                            <FieldError field={`child_id_${index}`} />
                          </div>
                          <div>
                            <Input type="date" placeholder="DOB" value={child.dob} onChange={(e) => updateChild(index, 'dob', e.target.value)} />
                            {child.id_number.length === 13 && child.dob && (
                              <p className="text-xs text-gray-400 mt-1">Auto-extracted from ID</p>
                            )}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Premium Calculation */}
            <Card className={calculatedPremium ? 'border-green-500' : ''}>
              <CardHeader><CardTitle className="text-lg">Premium Calculation</CardTitle></CardHeader>
              <CardContent className="space-y-4">
                <Button onClick={calculatePremium} disabled={calculating} className="w-full" variant="outline">
                  {calculating ? 'Calculating...' : 'Calculate Premium'}
                </Button>
                {calculatedPremium && (
                  <div className="bg-green-50 border border-green-200 rounded-lg p-4 space-y-2">
                    <div className="flex justify-between"><span>Base Premium:</span><span className="font-semibold">R{calculatedPremium.premium_breakdown.base_premium.toFixed(2)}</span></div>
                    {calculatedPremium.premium_breakdown.spouse_premium > 0 && (
                      <div className="flex justify-between"><span>Spouse Premium:</span><span className="font-semibold">R{calculatedPremium.premium_breakdown.spouse_premium.toFixed(2)}</span></div>
                    )}
                    {calculatedPremium.premium_breakdown.children_premium > 0 && (
                      <div className="flex justify-between"><span>Children Premium:</span><span className="font-semibold">R{calculatedPremium.premium_breakdown.children_premium.toFixed(2)}</span></div>
                    )}
                    <div className="border-t pt-2 flex justify-between text-lg font-bold">
                      <span>Total Premium:</span>
                      <span className="text-blue-600">R{calculatedPremium.premium_breakdown.total_premium.toFixed(2)}</span>
                    </div>
                    <div className="flex justify-between text-green-600 font-medium">
                      <span>Your Commission ({calculatedPremium.commission_split.merchant_rate}):</span>
                      <span>+R{calculatedPremium.commission_split.merchant_commission.toFixed(2)}</span>
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Submit */}
            <div className="flex gap-3">
              <Button variant="outline" className="flex-1" onClick={() => { setShowNewPolicyDialog(false); resetForm(); }}>Cancel</Button>
              <Button
                onClick={handleRequestPayment}
                disabled={!calculatedPremium || submitting || paymentConfirmed}
                className="flex-1 bg-green-600 hover:bg-green-700"
              >
                {paymentConfirmed ? 'Payment Confirmed ✓' : 'Collect Premium & Create Policy'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Payment Confirmation Dialog */}
      <Dialog open={showPaymentConfirmDialog} onOpenChange={setShowPaymentConfirmDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Collect Premium Payment</DialogTitle>
            <DialogDescription>Select how the customer is paying, then collect the premium before confirming</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 text-center">
              <p className="text-sm text-gray-600 mb-1">Amount Due</p>
              <p className="text-3xl font-bold text-blue-600">R{calculatedPremium?.premium_breakdown.total_premium.toFixed(2)}</p>
            </div>
            <div>
              <p className="text-sm font-medium text-gray-700 mb-2">Payment Method</p>
              <div className="grid grid-cols-3 gap-2">
                {[{ value: 'card', label: 'Card', Icon: CreditCard }, { value: 'cash', label: 'Cash', Icon: Banknote }, { value: 'wallet_qr', label: 'Wallet QR', Icon: QrCode }].map(({ value, label, Icon }) => (
                  <button key={value} type="button" onClick={() => setFormData(f => ({ ...f, payment_method: value }))}
                    className={`flex flex-col items-center gap-1 p-3 rounded-lg border-2 text-sm font-medium transition-colors ${formData.payment_method === value ? 'border-blue-600 bg-blue-50 text-blue-700' : 'border-gray-200 hover:border-gray-300 text-gray-600'}`}>
                    <Icon className="h-5 w-5" />{label}
                  </button>
                ))}
              </div>
            </div>
            {formData.payment_method === 'cash' && (() => {
              const premium = calculatedPremium?.premium_breakdown.total_premium ?? 0;
              const ok = walletBalance >= premium;
              return (
                <div className={`border rounded-lg p-3 text-sm ${ok ? 'bg-amber-50 border-amber-200 text-amber-800' : 'bg-red-50 border-red-300 text-red-800'}`}>
                  <div className="flex justify-between mb-1"><span>Your wallet balance:</span><span className="font-semibold">R{Number(walletBalance).toFixed(2)}</span></div>
                  <div className="flex justify-between mb-2"><span>Premium required:</span><span className="font-semibold">R{premium.toFixed(2)}</span></div>
                  {ok ? <p>Collect <strong>R{premium.toFixed(2)}</strong> cash from the customer before confirming.</p>
                    : <p><strong>Insufficient wallet balance.</strong> You need at least R{premium.toFixed(2)} to collect cash premiums. Please top up your wallet first.</p>}
                </div>
              );
            })()}
            {formData.payment_method === 'card' && (
              <div className="space-y-3">
                {cardPaymentTxnId ? (
                  <div className="bg-green-50 border border-green-300 rounded-lg p-3 text-sm text-green-800 flex items-center gap-2">
                    <CheckCircle className="h-4 w-4 shrink-0" />
                    <span>Card payment approved. Reference: <strong>{cardPaymentTxnId}</strong></span>
                  </div>
                ) : (
                  <div className="border rounded-lg p-3 space-y-3 bg-gray-50">
                    <p className="text-sm font-medium text-gray-700">Enter customer card details</p>
                    <div>
                      <Label className="text-xs">Card Number</Label>
                      <Input placeholder="1234 5678 9012 3456" maxLength={19} value={cardData.card_number}
                        onChange={(e) => { const val = e.target.value.replace(/\D/g, '').slice(0, 16); setCardData(c => ({ ...c, card_number: val.replace(/(.{4})/g, '$1 ').trim() })); }} />
                    </div>
                    <div className="grid grid-cols-3 gap-2">
                      <div><Label className="text-xs">Expiry (MM/YY)</Label>
                        <Input placeholder="12/27" maxLength={5} value={cardData.expiry_date}
                          onChange={(e) => { let v = e.target.value.replace(/\D/g, '').slice(0, 4); if (v.length >= 3) v = v.slice(0, 2) + '/' + v.slice(2); setCardData(c => ({ ...c, expiry_date: v })); }} />
                      </div>
                      <div><Label className="text-xs">CVV</Label>
                        <Input placeholder="123" maxLength={4} type="password" value={cardData.cvv}
                          onChange={(e) => setCardData(c => ({ ...c, cvv: e.target.value.replace(/\D/g, '').slice(0, 4) }))} />
                      </div>
                      <div><Label className="text-xs">Card Type</Label>
                        <Select value={cardData.card_type} onValueChange={(v) => setCardData(c => ({ ...c, card_type: v }))}>
                          <SelectTrigger className="h-10"><SelectValue /></SelectTrigger>
                          <SelectContent>
                            <SelectItem value="debit">Debit (2%)</SelectItem>
                            <SelectItem value="credit">Credit (3.5%)</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                    </div>
                    <Button onClick={handleCardPayment} disabled={processingCard || !cardData.card_number || !cardData.expiry_date || !cardData.cvv} className="w-full bg-blue-600 hover:bg-blue-700">
                      {processingCard ? 'Processing...' : `Charge R${calculatedPremium?.premium_breakdown.total_premium.toFixed(2)} to Card`}
                    </Button>
                  </div>
                )}
              </div>
            )}
            {formData.payment_method === 'wallet_qr' && (() => {
              const premium = calculatedPremium?.premium_breakdown.total_premium ?? 0;
              const ok = walletBalance >= premium;
              return (
                <div className={`border rounded-lg p-3 text-sm ${ok ? 'bg-green-50 border-green-200 text-green-800' : 'bg-red-50 border-red-300 text-red-800'}`}>
                  <div className="flex justify-between mb-1"><span>Your wallet balance:</span><span className="font-semibold">R{Number(walletBalance).toFixed(2)}</span></div>
                  <div className="flex justify-between mb-2"><span>Premium required:</span><span className="font-semibold">R{premium.toFixed(2)}</span></div>
                  {ok ? <p>Have the customer scan your wallet QR code to pay <strong>R{premium.toFixed(2)}</strong>. Once payment appears in your wallet, click confirm.</p>
                    : <p><strong>Insufficient wallet balance.</strong> Current balance: R{Number(walletBalance).toFixed(2)}.</p>}
                </div>
              );
            })()}
            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3">
              <p className="text-xs text-yellow-800"><strong>Important:</strong> Only confirm once you have successfully received the payment. The policy is created immediately upon confirmation.</p>
            </div>
          </div>
          <div className="flex gap-3">
            <Button variant="outline" className="flex-1" onClick={() => setShowPaymentConfirmDialog(false)}>Cancel</Button>
            <Button
              onClick={handlePaymentConfirmed}
              disabled={
                (['cash', 'wallet_qr'].includes(formData.payment_method) && walletBalance < (calculatedPremium?.premium_breakdown.total_premium ?? 0)) ||
                (formData.payment_method === 'card' && !cardPaymentTxnId)
              }
              className="flex-1 bg-green-600 hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {formData.payment_method === 'card' && !cardPaymentTxnId ? 'Process Card Payment First'
                : formData.payment_method === 'wallet_qr' && walletBalance < (calculatedPremium?.premium_breakdown.total_premium ?? 0) ? 'Awaiting QR Payment Settlement'
                  : '✓ Payment Received — Create Policy'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Insurance;
